import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiShield, FiRefreshCw, FiAlertTriangle, FiX } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";
import { COUNTRIES, countryName } from "../../utils/countries";
import { fetchKycSettings, saveStepScope } from "../../utils/kycSettingsApi";

/**
 * KYC Settings — which KYC steps users must complete, and in which countries.
 *
 * Each step is OFF, ON in all countries, or ON only in the selected countries
 * (more can be added later). The app decides each user's step from their
 * profile country against this, in GET /kyc/status. Step 1 starts KYC and is
 * always on everywhere. Steps not built yet (5, 6) ask for confirmation.
 */

const Toggle = ({ checked, onChange, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:opacity-40 ${checked ? "bg-emerald-500" : "bg-gray-300"}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
  </button>
);

/** "ON in all countries", "ON only in India, Bangladesh", "OFF". */
export const scopeSummary = (s) => {
  if (!s || !s.enabled) return "OFF";
  if (s.scope === "all") return s.excluded && s.excluded.length ? `ON in all countries except ${s.excluded.map(countryName).join(", ")}` : "ON in all countries";
  return `ON only in ${s.countries.map(countryName).join(", ")}`;
};

const draftOf = (s) => ({
  enabled: !!(s && s.enabled),
  scope: s && s.scope === "selected" ? "selected" : s && s.enabled ? "all" : "selected",
  countries: (s && s.countries) || [],
});

const sameDraft = (a, b) =>
  a.enabled === b.enabled && (!a.enabled || (a.scope === b.scope && (a.scope === "all" || JSON.stringify(a.countries) === JSON.stringify(b.countries))));

/** One step: switch, then All countries / Only selected countries, then Save. */
const StepRow = ({ step, scope, canEdit, onSaved }) => {
  const saved = draftOf(scope);
  const [draft, setDraft] = useState(saved);
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);
  const dirty = !sameDraft(draft, saved);
  const valid = !draft.enabled || draft.scope === "all" || draft.countries.length > 0;

  const save = async () => {
    if (draft.enabled && !step.implemented && !saved.enabled) {
      const sure = window.confirm(`Step ${step.n} (${step.label}) has no verification flow yet. While it is on, users cannot finish KYC. Switch it on anyway?`);
      if (!sure) return;
    }
    setSaving(true);
    try {
      const res = await saveStepScope(step.n, {
        enabled: draft.enabled,
        scope: draft.scope,
        countries: draft.enabled && draft.scope === "selected" ? draft.countries : [],
      });
      if (res.warnings && res.warnings.length) toast.error(res.warnings.join(" "));
      else toast.success(res.message || "Saved");
      onSaved(res.data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addCountry = (code) => {
    setPick("");
    if (code) setDraft({ ...draft, countries: [...draft.countries, code].sort() });
  };

  return (
    <div className="py-3 space-y-2" data-testid={`step-${step.n}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs min-w-0">
          <span className="font-semibold text-gray-800">
            Step {step.n} · {step.label}
          </span>
          {!step.implemented && (
            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold">
              <FiAlertTriangle size={10} /> Not built yet
            </span>
          )}
          <div className={`mt-0.5 ${scope && scope.enabled ? "text-emerald-700" : "text-gray-400"}`} data-testid={`step-${step.n}-summary`}>
            {step.required ? "Always ON in all countries" : scopeSummary(scope)}
          </div>
          {step.n === 4 && (
            <p className="text-[11px] text-gray-400 mt-0.5" data-testid="step4-note">
              The only place that sets which countries get phone verification. Users see it once the{" "}
              <a href="/sms-verification" className="text-blue-600 hover:underline">
                SMS Verification
              </a>{" "}
              page is switched on.
            </p>
          )}
        </div>
        <Toggle
          checked={step.required ? true : draft.enabled}
          disabled={!canEdit || saving || step.required}
          label={`Step ${step.n} on`}
          onChange={(on) => setDraft({ ...draft, enabled: on })}
        />
      </div>

      {!step.required && draft.enabled && (
        <div className="ml-1 pl-3 border-l-2 border-gray-100 space-y-2">
          <div className="flex flex-wrap gap-4 text-xs" role="radiogroup" aria-label={`Step ${step.n} countries`}>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`scope-${step.n}`}
                checked={draft.scope === "all"}
                disabled={!canEdit || saving}
                onChange={() => setDraft({ ...draft, scope: "all" })}
              />
              All countries
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`scope-${step.n}`}
                checked={draft.scope === "selected"}
                disabled={!canEdit || saving}
                onChange={() => setDraft({ ...draft, scope: "selected" })}
              />
              Only selected countries
            </label>
          </div>

          {draft.scope === "selected" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {draft.countries.length === 0 && <span className="text-[11px] text-red-500">Add at least one country</span>}
                {draft.countries.map((code) => (
                  <span key={code} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-medium">
                    {countryName(code)}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setDraft({ ...draft, countries: draft.countries.filter((c) => c !== code) })}
                        aria-label={`Remove ${countryName(code)} from step ${step.n}`}
                      >
                        <FiX size={11} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {canEdit && (
                <select
                  aria-label={`Add country to step ${step.n}`}
                  value={pick}
                  onChange={(e) => addCountry(e.target.value)}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs"
                >
                  <option value="">+ Add country</option>
                  {COUNTRIES.filter((c) => !draft.countries.includes(c.code)).map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {canEdit && dirty && (
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setDraft(saved)} disabled={saving} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!valid || saving}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-40"
          >
            {saving ? "Saving…" : `Save step ${step.n}`}
          </button>
        </div>
      )}
    </div>
  );
};

const KycSettings = () => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("kyc-settings", "edit");

  const [data, setData] = useState(null);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);

  const apply = (next) => {
    setData(next);
    setVersion((v) => v + 1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchKycSettings();
      setData(res.data);
      setVersion((v) => v + 1);
    } catch (err) {
      toast.error(err.message || "Could not load KYC settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const steps = data?.steps || [];
  const scopes = data?.stepScopes || [];

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-5xl mx-auto space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiShield className="text-blue-600" /> KYC Settings
                </h1>
                <p className="text-xs text-gray-400 mt-1 max-w-3xl">
                  Which KYC steps users must complete, and in which countries. A user gets a step when it is ON for their profile country. A step
                  switched off is skipped: the next step opens once the previous switched-on step is done.
                </p>
              </div>
              <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" aria-label="Refresh">
                <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl px-4 py-3">
              Switching a step on means users in those countries must complete it to count as "KYC complete", including for exchanges that require
              KYC.
            </div>

            {data && (
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="kyc-steps">
                <h2 className="text-sm font-bold text-gray-800">Steps</h2>
                <div className="divide-y divide-gray-50">
                  {steps.map((step) => (
                    <StepRow
                      key={`${step.n}-${version}`}
                      step={step}
                      scope={scopes.find((s) => s.n === step.n)}
                      canEdit={canEdit}
                      onSaved={apply}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default KycSettings;
